/**
 * Step Confirmation Card Component
 *
 * Displays the data collected for a wizard step and asks for user confirmation.
 * This is the core of the human-in-the-loop experience.
 */

import React from 'react';
import { Box, Paper, Typography, Button, Chip, Divider, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import useWizardStore from '../wizard.store';
import { CampaignWizardStep, CAMPAIGN_WIZARD_STEPS } from '../wizard.types';

interface StepConfirmationCardProps {
  step: CampaignWizardStep;
  title?: string;
  children: React.ReactNode;
  showNavigation?: boolean;
  onConfirm?: () => void;
  onEdit?: () => void;
  confirmLabel?: string;
  editLabel?: string;
}

const StepConfirmationCard: React.FC<StepConfirmationCardProps> = ({
  step,
  title,
  children,
  showNavigation = true,
  onConfirm,
  onEdit,
  confirmLabel,
  editLabel,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const { activeWizard, confirmStep, editStep, previousStep } = useWizardStore();

  if (!activeWizard) return null;

  const stepDef = CAMPAIGN_WIZARD_STEPS.find(s => s.id === step);
  const stepState = activeWizard.steps[step];
  const isConfirmed = stepState.status === 'confirmed';

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    } else {
      confirmStep(step);
    }
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
    } else {
      editStep(step);
    }
  };

  const stepIndex = CAMPAIGN_WIZARD_STEPS.findIndex(s => s.id === step);
  const canGoBack = stepIndex > 0;

  return (
    <Paper className={cx(classes.card, isConfirmed && 'confirmed')}>
      <Box className={classes.header}>
        <Box className={classes.headerContent}>
          <Chip
            label={`${t('step', 'Step')} ${stepIndex + 1}`}
            size="small"
            color={isConfirmed ? 'success' : 'primary'}
            variant={isConfirmed ? 'filled' : 'outlined'}
            className={classes.stepChip}
          />
          <Typography variant="subtitle1" className={classes.title}>
            {title || t(step, stepDef?.name || step)}
          </Typography>
        </Box>
        {isConfirmed && (
          <Tooltip title={t('confirmed', 'Confirmed')}>
            <CheckCircleIcon className={classes.confirmedIcon} />
          </Tooltip>
        )}
      </Box>

      <Typography variant="caption" color="textSecondary" className={classes.description}>
        {t(step + '_desc', { defaultValue: stepDef?.description || '' })}
      </Typography>

      <Divider className={classes.divider} />

      <Box className={classes.content}>{children}</Box>

      <Divider className={classes.divider} />

      <Box className={classes.actions}>
        {showNavigation && canGoBack && (
          <Button
            variant="text"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={previousStep}
            className={classes.backButton}
          >
            {t('back', 'Back')}
          </Button>
        )}

        <Box className={classes.primaryActions}>
          {isConfirmed ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={handleEdit}
            >
              {editLabel || t('edit', 'Edit')}
            </Button>
          ) : (
            <>
              <Button variant="text" size="small" onClick={handleEdit}>
                {editLabel || t('makeChanges', 'Make Changes')}
              </Button>
              <Button
                variant="contained"
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={handleConfirm}
                color="primary"
              >
                {confirmLabel || t('confirmAndContinue', 'Confirm & Continue')}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

/**
 * Data Row Component for displaying key-value pairs in confirmation cards
 */
interface DataRowProps {
  label: string;
  value: React.ReactNode;
  emptyText?: string;
}

export const DataRow: React.FC<DataRowProps> = ({ label, value, emptyText = 'Not set' }) => {
  const { classes, cx } = useStyles();

  const isEmpty = value === undefined || value === null || value === '' ||
    (Array.isArray(value) && value.length === 0);

  return (
    <Box className={classes.dataRow}>
      <Typography variant="caption" color="textSecondary" className={classes.dataLabel}>
        {label}
      </Typography>
      <Typography variant="body2" className={cx(classes.dataValue, isEmpty && 'empty')}>
        {isEmpty ? emptyText : (
          Array.isArray(value) ? value.join(', ') : value
        )}
      </Typography>
    </Box>
  );
};

/**
 * Data Section Component for grouping related data rows
 */
interface DataSectionProps {
  title: string;
  children: React.ReactNode;
}

export const DataSection: React.FC<DataSectionProps> = ({ title, children }) => {
  const { classes } = useStyles();

  return (
    <Box className={classes.dataSection}>
      <Typography variant="overline" className={classes.sectionTitle}>
        {title}
      </Typography>
      {children}
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  card: {
    padding: theme.spacing(2),
    borderRadius: 12,
    border: `2px solid ${theme.palette.primary.main}`,
    backgroundColor: '#fff',
    transition: 'border-color 0.2s',
    '&.confirmed': {
      borderColor: theme.palette.success.main,
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(0.5),
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  stepChip: {
    height: 22,
    fontSize: '0.65rem',
    fontWeight: 600,
  },
  title: {
    fontWeight: 600,
  },
  confirmedIcon: {
    color: theme.palette.success.main,
    fontSize: '1.5rem',
  },
  description: {
    display: 'block',
    marginBottom: theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(1.5, 0),
  },
  content: {
    minHeight: 60,
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  backButton: {
    color: theme.palette.text.secondary,
  },
  primaryActions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginLeft: 'auto',
  },
  dataRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  dataLabel: {
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontSize: '0.65rem',
  },
  dataValue: {
    fontWeight: 500,
    '&.empty': {
      color: theme.palette.text.disabled,
      fontStyle: 'italic',
    },
  },
  dataSection: {
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  sectionTitle: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
}));

export default StepConfirmationCard;

